'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import DocumentTab from 'src/parts/tabs/DocumentTab';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getEventDocuments } from 'src/api/personal/eventDetail';
import { getDocumentTypes } from 'src/api/admin/documentTypes';
import { getEvent } from 'src/api/personal/events';

interface DocumentsSearch {
	filter?: string;
	page?: number;
}

export const Route = createFileRoute('/personal_/events/$eventId/documents/')({
	validateSearch: (search: Record<string, unknown>): DocumentsSearch => ({
		filter: search.filter as string | undefined,
		page: (search.page as number) || undefined,
	}),
	component: DocumentsPage,
});

function DocumentsPage() {
	const { eventId } = Route.useParams();
	const searchParams = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const hasAllEventsView = verifyPermission([PERMISSIONS.personalAllEventsView]);
	const hasOwnEventsView = verifyPermission([PERMISSIONS.personalOwnEventsView]);

	// Fetch event to determine actual ownership
	const { data: event, isLoading: eventLoading } = useQuery({
		queryKey: ['personal-event', eventId],
		queryFn: () => getEvent(eventId, !hasAllEventsView),
	});

	// Check if user is actually the owner of the event
	const isOwner = event?.adminIds?.includes(authenticatedUser?.id || '');
	const asOwner = isOwner && hasOwnEventsView;

	const { data: documentsData, isLoading: documentsLoading } = useQuery({
		queryKey: ['personal-event-documents', eventId, asOwner, searchParams],
		queryFn: () => getEventDocuments(eventId, asOwner, { filter: searchParams.filter, page: searchParams.page }),
		enabled: !!event, // Only fetch documents once we have the event
	});

	const { data: documentTypesData, isLoading: typesLoading } = useQuery({
		queryKey: ['document-types', 'events'],
		queryFn: () => getDocumentTypes('events'),
	});

	const revalidateEventDetail = () => {
		queryClient.invalidateQueries({ queryKey: ['personal-event-documents', eventId] });
		queryClient.invalidateQueries({ queryKey: ['personal-event', eventId] });
	};

	const handleReload = () => revalidateEventDetail();

	const handleSelectDocument = (id: string) => {
		navigate({
			to: '/personal/events/$eventId/documents/$documentId',
			params: { eventId, documentId: id },
		});
	};

	const apiUrl = asOwner
		? `/api/personal-dashboard/own-events/${eventId}/documents`
		: `/api/personal-dashboard/events/${eventId}/documents`;

	const isLoading = authLoading || eventLoading || documentsLoading || typesLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	return (
		<DocumentTab
			data={documentsData}
			documentTypes={documentTypesData?.data || []}
			revalidateDetail={revalidateEventDetail}
			apiUrl={apiUrl}
			onReload={handleReload}
			onSelectDocument={handleSelectDocument}
			permissions={{
				metadata: (hasOwnEventsView && isOwner) || hasAllEventsView,
				fullView: (hasOwnEventsView && isOwner) || hasAllEventsView,
				upload:
					(verifyPermission([PERMISSIONS.personalOwnEventsEdit]) && isOwner) ||
					verifyPermission([PERMISSIONS.personalAllEventsEdit]),
				archive:
					(verifyPermission([PERMISSIONS.personalOwnEventsEdit]) && isOwner) ||
					verifyPermission([PERMISSIONS.personalAllEventsEdit]),
				edit:
					(verifyPermission([PERMISSIONS.personalOwnEventsEdit]) && isOwner) ||
					verifyPermission([PERMISSIONS.personalAllEventsEdit]),
			}}
		/>
	);
}
